// API Service for Agent Router and Anthropic Integration
export interface ApiConfig {
  anthropicApiKey: string;
  anthropicAuthToken: string;
  anthropicBaseUrl: string;
  agentRouterUrl: string;
  agentRouterApiKey: string;
  environment: string;
  googleSheetsWebAppUrl: string;
}

const getApiConfig = (): ApiConfig => {
  return {
    anthropicApiKey: process.env.REACT_APP_ANTHROPIC_API_KEY || process.env.ANTHROPIC_API_KEY || '',
    anthropicAuthToken: process.env.ANTHROPIC_AUTH_TOKEN || '',
    anthropicBaseUrl: process.env.ANTHROPIC_BASE_URL || 'https://agentrouter.org/',
    agentRouterUrl: process.env.REACT_APP_AGENT_ROUTER_URL || 'https://agentrouter.org/',
    agentRouterApiKey: process.env.REACT_APP_AGENT_ROUTER_API_KEY || process.env.ANTHROPIC_AUTH_TOKEN || '',
    environment: process.env.REACT_APP_ENV || 'development',
    googleSheetsWebAppUrl: process.env.REACT_APP_GOOGLE_SHEETS_WEB_APP_URL || '',
  };
};

export const apiConfig = getApiConfig();

export const agentRouterService = {
  /**
   * Send a request to the Agent Router Cloud
   */
  async sendRequest(endpoint: string, data: any) {
    try {
      const url = `${apiConfig.agentRouterUrl.replace(/\/$/, '')}${endpoint}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiConfig.agentRouterApiKey}`,
          'x-api-key': apiConfig.agentRouterApiKey,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Agent Router Error: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Agent Router Request Failed:', error);
      throw error;
    }
  },

  /**
   * Process course enrollment through Agent Router
   */
  async enrollCourse(userId: string, courseId: string) {
    return this.sendRequest('/api/enroll', { userId, courseId });
  },

  /**
   * Get user courses
   */
  async getUserCourses(userId: string) {
    return this.sendRequest('/api/courses/user', { userId });
  },

  /**
   * Submit contact form through Agent Router
   */
  async submitContactForm(data: { name: string; email: string; message: string }) {
    return this.sendRequest('/api/contact', data);
  },
};

export interface EnrollmentFormData {
  studentName: string;
  email: string;
  gender: string;
  birthDate: string;
  phoneCode: string;
  phoneNumber: string;
  school: string;
  grade: string;
  city: string;
  studentCategory: string;
  educationSystem: string;
  firstLanguage: string;
  secondLanguage: string;
  lastGrade: string;
}

export const googleSheetsService = {
  /**
   * Submit the contact/enrollment form to the Google Sheet via the
   * Apps Script Web App (see google-apps-script/README.md for setup).
   * Uses a text/plain content type to keep the request a CORS "simple
   * request" — Apps Script Web Apps don't handle preflight OPTIONS calls.
   */
  async submitEnrollment(data: EnrollmentFormData) {
    if (!apiConfig.googleSheetsWebAppUrl) {
      throw new Error('Google Sheets Web App URL is not configured');
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(apiConfig.googleSheetsWebAppUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8',
        },
        body: JSON.stringify(data),
        signal: controller.signal,
      });

      if (process.env.NODE_ENV !== 'production') {
        console.debug('[googleSheetsService] response', {
          status: response.status,
          ok: response.ok,
          type: response.type,
          redirected: response.redirected,
          url: response.url,
          headers: Object.fromEntries(response.headers.entries()),
        });
      }

      // response.type === 'opaque' only happens with mode: 'no-cors', which
      // this request never sets — a same-type failure here would mean the
      // Web App stopped sending Access-Control-Allow-Origin, not a real error.
      if (response.type === 'opaque' || response.type === 'opaqueredirect') {
        throw new Error(`Unexpected opaque response (type: ${response.type})`);
      }

      if (!response.ok) {
        throw new Error(`Google Sheets Error: ${response.status}`);
      }

      const result = await response.json();

      if (process.env.NODE_ENV !== 'production') {
        console.debug('[googleSheetsService] parsed JSON', result);
      }

      if (result?.result !== 'success') {
        throw new Error(result?.error || 'Google Sheets submission failed');
      }

      return result;
    } finally {
      clearTimeout(timeout);
    }
  },
};

export const anthropicService = {
  /**
   * Call Anthropic API through Agent Router Cloud
   */
  async callClaude(prompt: string, model: string = 'claude-3-sonnet-20240229') {
    try {
      // Use Agent Router as the proxy for Anthropic API
      const apiUrl = apiConfig.anthropicBaseUrl.replace(/\/$/, '');
      const response = await fetch(`${apiUrl}/v1/messages`, {
        method: 'POST',
        headers: {
          'x-api-key': apiConfig.anthropicAuthToken || apiConfig.anthropicApiKey,
          'anthropic-version': '2023-06-01',
          'content-type': 'application/json',
          'Authorization': `Bearer ${apiConfig.anthropicAuthToken || apiConfig.anthropicApiKey}`,
        },
        body: JSON.stringify({
          model,
          max_tokens: 1024,
          messages: [
            {
              role: 'user',
              content: prompt,
            },
          ],
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Anthropic API Error: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Anthropic API Request Failed:', error);
      throw error;
    }
  },
};
